import React, {Component} from 'react'
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import moment from "moment";
import {Button, Col, Container, Row} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ClaimStatusEnum from "./ClaimStatus";
import {ColumnGroup} from 'primereact/columngroup';
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import ClaimActivities from "./ClaimActivities";
import {Modal, SelectInput, TextInput, XmlPreviewModal} from "../../../../../components";
import HoverableDescriptionBox from "../../../../../components/hoverable-desc-box/HoverableDescriptionBox";
import DateUtil from "../../../../../utils/DateUtil";
import EligibilityStatusEnum from "../../../enums/EligibilityStatusEnum";
import dialog from "../../../../../components/dialog/Dialog";
import ActionMenu from "../../../../../components/action-menu/ActionMenu";
import ActionMenuItem from "../../../../../components/action-menu/ActionMenuItem";
import PriorAuthorizationUpdateModal from "./PriorAuthorizationUpdateModal";
import ClaimStatusUpdateModal from "./ClaimStatusUpdateModal";
import ClaimStatusLogModal from "./ClaimStatusLogModal";
import EclaimTransactionLogModal from "./EclaimTransactionLogModal";
import ClaimCheckerResultDialog from "./ClaimCheckerResultDialog";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import AuthService from "../../../../../services/auth-service/AuthService";
import EntityNoteListModal from "../notes/EntityNoteListModal";

const emptyFilters = {
    claimId: '', memberId: '', payerId: '', receiverId: '',
    emiratesId: '', invoiceNumber: '', speciality: '',
    facilityId: '', mrn: '', status: ''
};

export default class ClaimList extends Component {

    constructor(props) {
        super(props);
        this.dataTableRef = React.createRef();
        this.state = {
            items: [],
            errorMessages: [],
            dateRange: [new Date(), new Date()],
            expandedClaims: null,
            selectedItems: [],
            editClaimIdModalVisible: false,
            editingClaim: null,
            newClaimId: '',
            xmlPreviewModalVisible: false,
            previewXml: '',
            editedXml: '',
            submitting: false,
            checking: false,
            claimCheckResult: null,
            claimCheckDialogVisible: false,
            page: 1,
            rows: 10,
            totalCount: 0,
            filters: {...emptyFilters}
        };
    }

    componentDidMount() {
        this.retrieveItems();
    }

    componentDidUpdate(prevProps) {
        if (this.props.claimFocus && this.props.claimFocus !== prevProps.claimFocus) {
            this.focusClaim(this.props.claimFocus.claimId);
        } else if (!prevProps.selected && this.props.selected) {
            this.retrieveItems();
        }
    }

    // Opened from a remittance: only that claim, whatever its date; picking dates again lists as usual
    focusClaim = (claimId) => {
        this.setState({filters: {...emptyFilters, claimId}, dateRange: null, page: 1}, this.retrieveItems);
    }

    componentWillUnmount() {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({dateRange, page: 1}, () => {
            // Only fetch when both start and end dates are selected
            if (dateRange && dateRange[0] && dateRange[1]) {
                this.retrieveItems();
            }
        });
    }

    // Text filters fire on every keystroke; debounce the backend fetch so we
    // don't send a request per character.
    debouncedRetrieve = () => {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
        this.filterDebounce = setTimeout(() => {
            this.retrieveItems();
        }, 400);
    }

    handleFilterChange = (field, value) => {
        const isTextFilter = field !== 'status';
        this.setState({
            filters: {...this.state.filters, [field]: value},
            page: 1
        }, () => {
            if (isTextFilter) {
                this.debouncedRetrieve();
            } else {
                this.retrieveItems();
            }
        });
    }

    retrieveItems = () => {
        const [fromDate, toDate] = this.state.dateRange || [];
        const hasDates = fromDate && toDate;
        // Without dates only a claim id search runs (a claim opened from its remittance)
        if (!hasDates && !this.state.filters.claimId) {
            return;
        }

        const params = {
            page: this.state.page,
            size: this.state.rows,
            ...this.props.listParams
        };
        if (hasDates) {
            params.fromDate = moment(fromDate).format('YYYY-MM-DD');
            params.toDate = moment(toDate).format('YYYY-MM-DD');
        }
        // Only send non-empty filters so they combine with AND on the backend.
        const f = this.state.filters;
        if (f.claimId) params.claimId = f.claimId;
        if (f.memberId) params.memberId = f.memberId;
        if (f.payerId) params.payerId = f.payerId;
        if (f.receiverId) params.receiverId = f.receiverId;
        if (f.emiratesId) params.emiratesId = f.emiratesId;
        if (f.invoiceNumber) params.invoiceNumber = f.invoiceNumber;
        if (f.speciality) params.speciality = f.speciality;
        if (f.facilityId) params.facilityId = f.facilityId;
        if (f.mrn) params.mrn = f.mrn;
        if (f.status) params.status = f.status;

        RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/claims',
            params,
            (result) => {
                const items = result?.items || [];
                this.setState({
                    items: items,
                    totalCount: result?.totalCount || 0,
                    page: result?.currentPage || this.state.page,
                    // The row set has been replaced (page/filter/date change, or a
                    // refresh after submission), so anything still selected is a claim
                    // from the previous set that is no longer on screen. Drop it,
                    // otherwise Submit/Check/Preview would act on invisible claims.
                    selectedItems: []
                });
            });
    }

    showSubmissionResult = (result) => {
        if (result.success) {
            NotificationService.showNotification({severity: 'success', summary: 'Success', detail: result.message});
            this.retrieveItems(); // a blocked or failed submission changes nothing worth the slow reload
        } else if (result.audits && result.audits.length > 0) {
            // Blocked by the internal audit: show the failed checks instead of a toast
            this.setState({claimCheckResult: result, claimCheckDialogVisible: true, claimCheckDialogHeader: 'Claim Submission'});
        } else {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Submission Failed',
                detail: result.message + (result.errorReport ? `\n${result.errorReport}` : '')
            });
        }
    }

    submitClaims = () => {
        const selectedClaimIds = this.state.selectedItems.map(claim => claim.id);
        this.setState({submitting: true});
        RemotingService.postRemoteCall(`api/insurance/coordinator/v2/claims/eclaim-submission`,
            {claimIds: selectedClaimIds, editedXml: null},
            (result) => {
                this.setState({submitting: false});
                this.showSubmissionResult(result);
            }, () => {
                this.setState({submitting: false});
            });
    }

    checkClaims = () => {
        const selectedClaimIds = this.state.selectedItems.map(claim => claim.id);
        this.setState({checking: true});
        RemotingService.postRemoteCall(`api/insurance/coordinator/v2/claims/claim-check`,
            {claimIds: selectedClaimIds, editedXml: null},
            (result) => {
                this.setState({checking: false, claimCheckResult: result, claimCheckDialogVisible: true, claimCheckDialogHeader: 'Claim Check'});
            }, () => {
                this.setState({checking: false});
            });
    }

    generateXmlPreview = () => {
        const selectedClaimIds = this.state.selectedItems.map(claim => claim.id);
        RemotingService.postRemoteCall(`api/insurance/coordinator/v2/claims/xml-preview`, selectedClaimIds, (result) => {
            this.setState({
                xmlPreviewModalVisible: true,
                previewXml: result.xmlContent,
                editedXml: result.xmlContent
            });
        }, () => {});
    }

    submitToEClaim = () => {
        const selectedClaimIds = this.state.selectedItems.map(claim => claim.id);
        const submitDTO = {
            claimIds: selectedClaimIds,
            editedXml: this.state.editedXml !== this.state.previewXml ? this.state.editedXml : null
        };

        this.setState({submitting: true});
        RemotingService.postRemoteCall(`api/insurance/coordinator/v2/claims/eclaim-submission`, submitDTO, (result) => {
            this.setState({
                xmlPreviewModalVisible: false,
                submitting: false,
                previewXml: '',
                editedXml: ''
            });
            this.showSubmissionResult(result);
        }, () => {
            this.setState({submitting: false});
        });
    }

    renderXmlPreviewModal = () => {
        const ts = moment().format("YYYYMMDD_HHmmss");
        return (
            <XmlPreviewModal
                visible={this.state.xmlPreviewModalVisible}
                header="Claim XML Preview"
                xml={this.state.editedXml}
                onXmlChange={(value) => this.setState({editedXml: value})}
                downloadFilename={`ClaimSubmission_${ts}.xml`}
                onSubmit={() => this.submitToEClaim()}
                submitting={this.state.submitting}
                onClose={() => this.setState({xmlPreviewModalVisible: false, previewXml: '', editedXml: ''})}
            />
        );
    }

    textFilter = (field) => {
        return <input type="text" className="w-100"
                      value={this.state.filters[field]}
                      onChange={e => this.handleFilterChange(field, e.target.value)}/>;
    }

    renderHeaders = () => {
        return <ColumnGroup>
            <Row>
                <Column selectionMode="multiple" headerStyle={{width: '3em'}} />
                <Column header="" colSpan={1}/>
                <Column header="Claim ID" colSpan={1}/>
                <Column header="Member ID" colSpan={1}/>
                <Column header="Payer ID" colSpan={1}/>
                <Column header="Receiver ID" colSpan={1}/>
                <Column header="Emirates ID" colSpan={1}/>
                <Column header="Invoice No" colSpan={1}/>
                <Column header="Speciality" colSpan={1}/>
                <Column header="Gross" colSpan={1}/>
                <Column header="Patient Payable" colSpan={1}/>
                <Column header="Net" colSpan={1}/>
                <Column header="Encounter" colSpan={5}/>
                <Column header="Diagnosis Group" colSpan={1}/>
                <Column header="Prior Auth No" colSpan={1}/>
                <Column header="Eligibility" colSpan={1}/>
                <Column header="Note" colSpan={1}/>
                <Column header="Status" colSpan={1}/>
                <Column header="Actions" colSpan={1}/>
            </Row>
            <Row>
                <Column header="" colSpan={1}/>
                <Column header="" headerStyle={{width: '40px'}}/>
                <Column field="claimId" filter filterElement={this.textFilter('claimId')}/>
                <Column field="memberId" filter filterElement={this.textFilter('memberId')}/>
                <Column field="payerId" filter filterElement={this.textFilter('payerId')}/>
                <Column field="receiverId" filter filterElement={this.textFilter('receiverId')}/>
                <Column field="emiratesId" filter filterElement={this.textFilter('emiratesId')}/>
                <Column field="invoiceNumber" filter filterElement={this.textFilter('invoiceNumber')}/>
                <Column field="speciality" filter filterElement={this.textFilter('speciality')}/>
                <Column field="gross"/>
                <Column field="patientPayable"/>
                <Column field="net"/>

                <Column header="Facility ID" field="facilityId" filter filterElement={this.textFilter('facilityId')}/>
                <Column header="Type" field="type"/>
                <Column header="Patient ID" field="mrn" filter filterElement={this.textFilter('mrn')}/>
                <Column header="Start" field="start"/>
                <Column header="End" field="end"/>


                <Column field="diagnosisGroup" body={this.diagnosisTemplate}/>

                <Column field="priorAuthorizationId"/>
                <Column field="eligibilityStatus"/>
                <Column header="" colSpan={1}/>

                <Column field="status" filter
                        filterElement={<SelectInput className="w-100" displayProperty="name" valueProperty="key"
                                                    options={ClaimStatusEnum.toArray()} searchEnabled={false}
                                                    value={this.state.filters.status}
                                                    onChange={event => this.handleFilterChange('status', event)}
                        />}/>

                <Column header="" colSpan={1}/>
            </Row>
        </ColumnGroup>;
    }


    claimRecordExpansionTemplate(claim) {
        return (
            <div className="appointment-expansion-wrapper row" style={{padding: "20px", animation: "fadeAnimation 1s"}}>
                <div className="col-12">
                    <ClaimActivities claim={claim} onClaimSubmitted={this.retrieveItems}/>
                </div>
            </div>
        );
    }

    // DHA has no medical tourism payers: the list shows the payer the claim XML sends
    payerIdTemplate(item) {
        return item.payerId;
    }

    statusTemplate(item) {
        return ClaimStatusEnum[item.status].name;
    }

    eligibilityStatusTemplate(item) {
        return item.eligibilityStatus ? EligibilityStatusEnum[item.eligibilityStatus].name : '';
    }

    openPriorAuthorizationModal(item) {
        dialog({
            title: "Update Prior Authorization",
            component: <PriorAuthorizationUpdateModal claimId={item.id}
                                                      priorAuthorizationId={item.priorAuthorizationId}
                                                      onSaved={this.retrieveItems}/>
        });
    }

    openNotesModal = (item) => {
        dialog({
            title: "Claim Notes",
            component: <EntityNoteListModal entityId={item.id}
                                            baseUrl={"api/insurance/coordinator/v2/claims/claim"}
                                            readOnly={!AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM")}
                                            onNotesChange={() => this.retrieveItems()}/>,
            size: "lg"
        });
    }

    notesTemplate = (item) => {
        const notes = item.notes || [];
        const notesCount = notes.filter(note => note.isActive !== false).length;
        return <Button variant={"link"} style={{color: "#007bff", fontSize: "14px"}}
                       onClick={() => this.openNotesModal(item)}>
            <FontAwesomeIcon icon={["fas", "sticky-note"]} size={"sm"}/> Notes ({notesCount})
        </Button>;
    }

    openEditClaimIdModal = (item) => {
        this.setState({
            editClaimIdModalVisible: true,
            editingClaim: item,
            newClaimId: item.claimId || ''
        });
    };

    closeEditClaimIdModal = () => {
        this.setState({editClaimIdModalVisible: false, editingClaim: null, newClaimId: ''});
    };

    updateClaimId = () => {
        RemotingService.postRemoteCall(
            `api/insurance/coordinator/v2/claims/claim/${this.state.editingClaim.id}/update-claim-id`,
            this.state.newClaimId,
            () => {
                NotificationService.showNotification({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Claim ID updated successfully'
                });
                this.closeEditClaimIdModal();
                this.retrieveItems();
            }
        );
    };

    renderEditClaimIdModal = () => {
        return (
            <Modal visible={this.state.editClaimIdModalVisible}
                   header="Edit Claim ID"
                   submitContent="Update Claim ID"
                   submitAction={() => this.updateClaimId()}
                   closeAction={this.closeEditClaimIdModal}
                   submitDisabled={!this.state.newClaimId || this.state.newClaimId.trim() === ''}
                   contentStyle={{width: "400px", height: "150px"}}>
                <Container className={"InputModal"}>
                    <Row>
                        <Col xs={3} className="dim14-form-label">
                            Claim ID
                        </Col>
                        <Col xs={9}>
                            <TextInput
                                style={{width: "100%"}}
                                value={this.state.newClaimId}
                                onChange={(value) => this.setState({newClaimId: value})}
                            />
                        </Col>
                    </Row>
                </Container>
            </Modal>
        );
    };

    openStatusUpdateModal(item) {
        dialog({
            title: "Update Claim Status",
            component: <ClaimStatusUpdateModal claimId={item.id}
                                               status={item.status}
                                               onSaved={this.retrieveItems}/>
        });
    }

    openStatusLogModal(item) {
        dialog({
            title: "Status History",
            component: <ClaimStatusLogModal claimId={item.id}/>
        });
    }

    openEclaimLogModal(item) {
        dialog({
            title: "Transaction History",
            component: <EclaimTransactionLogModal claimId={item.id}/>
        });
    }

    openTransactionLogModal = () => {
        dialog({
            title: "Transaction History",
            component: <EclaimTransactionLogModal url="api/insurance/coordinator/v2/claims/eclaim-transaction-log"
                                                  showClaimId/>
        });
    }

    renderActions = (item) => {
        return (
            <ActionMenu id="claimactionmenu">
                <ActionMenuItem text="Update Prior Authorization"
                                onClick={() => this.openPriorAuthorizationModal(item)}/>
                <ActionMenuItem text="Edit Claim ID"
                                onClick={() => this.openEditClaimIdModal(item)}/>
                <ActionMenuItem text="Update Status"
                                onClick={() => this.openStatusUpdateModal(item)}/>
                <ActionMenuItem text="Status History"
                                onClick={() => this.openStatusLogModal(item)}/>
                <ActionMenuItem text="Submission History"
                                onClick={() => this.openEclaimLogModal(item)}/>
            </ActionMenu>
        );
    }

    diagnosisTemplate = (item) => {
        return (
            <div className="icd-cell">
                {item.diagnosisGroup?.diagnosisItems?.map((diagItem, index) => {
                    const prefix = diagItem.level === "PRINCIPAL" ? "P: " : "S: ";
                    const header = `${prefix}${diagItem.code}`;
                    
                    return <HoverableDescriptionBox 
                        key={`dg-${index}`}
                        header={header} 
                        description={diagItem.description ? diagItem.description : diagItem.code}
                        className="icd-box"
                    />
                })}
            </div>
        );
    }

    render() {

        return (
            <>
                <div className="float-left " style={{display: "flex"}}>
                    <PrimeDateRangeInput value={this.state.dateRange}
                                         onChange={this.handleDateRangeChange}/>
                </div>
                <div className="float-right" style={{display: "flex", gap: "10px"}}>
                    <Button variant="light"
                            onClick={this.openTransactionLogModal}>
                        <FontAwesomeIcon icon="history"/> Transaction Log
                    </Button>
                    <Button disabled={this.state.selectedItems.length === 0 || this.state.checking}
                            variant="warning"
                            onClick={() => this.checkClaims()}>
                        <FontAwesomeIcon icon="check-double"/> {this.state.checking ? 'Checking...' : 'Claim Check'}
                    </Button>
                    <Button disabled={this.state.selectedItems.length === 0}
                            variant="success"
                            onClick={() => this.generateXmlPreview()}>
                        Preview XML
                    </Button>
                    <Button disabled={this.state.selectedItems.length === 0 || this.state.submitting}
                            variant="success"
                            onClick={() => this.submitClaims()}>
                        {this.state.submitting ? 'Submitting...' : 'Submit Claims'}
                    </Button>
                </div>
                <div style={{padding: '10px'}} className="data-table-header-wrapper">
                    <div className="float-left">
                    </div>
                </div>
                <div style={{padding: '10px'}}>
                    <DataTable className="claimListTable" responsive headerColumnGroup={this.renderHeaders()}
                               emptyMessage={"No items found"}
                               scrollable ref={this.dataTableRef}
                               value={this.state.items}
                               paginator lazy
                               first={(this.state.page - 1) * this.state.rows}
                               totalRecords={this.state.totalCount}
                               rows={this.state.rows}
                               rowsPerPageOptions={[10, 25, 50, 100]}
                               onPage={(e) => this.setState({page: (e.page ?? 0) + 1, rows: e.rows}, () => this.retrieveItems())}
                               paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                               currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                               onRowToggle={(e) => {
                                   this.setState({
                                       ...this.state,
                                       expandedClaims: e.data
                                   })
                               }}
                               expandedRows={this.state.expandedClaims}
                               rowExpansionTemplate={(row) => this.claimRecordExpansionTemplate(row)}
                               dataKey="id"
                               selection={this.state.selectedItems}
                               onSelectionChange={e => this.setState({selectedItems: e.value})}>
                        <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                        <Column expander={true} style={{width: '30px'}}/>
                        <Column field="claimId" headerStyle={{width: '200px'}} style={{width: '200px', wordBreak: "break-all"}}/>
                        <Column field="memberId" style={{width: "7%" , wordWrap : "break-word"}}/>
                        <Column field="payerId" body={this.payerIdTemplate} style={{wordWrap: "break-word"}}/>
                        <Column field="receiverId"/>
                        <Column field="emiratesId" style={{width: "7%" , wordWrap : "break-word"}}/>
                        <Column field="invoiceNumber"/>
                        <Column field="speciality" style={{wordWrap: "break-word", wordBreak: "break-word"}}/>
                        <Column field="gross"/>
                        <Column field="patientPayable"/>
                        <Column field="net"/>
                        <Column field="facilityId"/>
                        <Column field="type"/>
                        <Column field="mrn" style={{wordWrap: "break-word", wordBreak: "break-all"}}/>
                        <Column field="start" body={item => item.start ? DateUtil.formatDateTime12H(item.start) : ''}/>
                        <Column field="end" body={item => item.end ? DateUtil.formatDateTime12H(item.end) : ''}/>
                        <Column field="diagnosisGroup" body={this.diagnosisTemplate}/>
                        <Column field="priorAuthorizationId" style={{wordWrap: "break-word", wordBreak: "break-all"}}/>
                        <Column field="eligibilityStatus" body={this.eligibilityStatusTemplate}/>
                        <Column header="Note" body={this.notesTemplate}/>
                        <Column field="status" body={this.statusTemplate}/>
                        <Column header="Actions" body={this.renderActions} style={{textAlign: "center", width: "75px"}}/>
                    </DataTable>
                </div>
                {this.renderEditClaimIdModal()}
                {this.renderXmlPreviewModal()}
                <ClaimCheckerResultDialog header={this.state.claimCheckDialogHeader} visible={this.state.claimCheckDialogVisible}
                                          result={this.state.claimCheckResult}
                                          onClose={() => this.setState({claimCheckDialogVisible: false})}/>
            </>
        )
    }
}
